import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useScanResults } from '@hooks/useScanResults';
import { useSession } from '@hooks/useSession';

import { ObservationRecord } from '@models/observation';

import { useListDeviceConnectionsQuery } from 'generated/scan.generated';
import {
  useGetScanProfileQuery,
  useListScanResultsQuery,
  useUpsertScanProfileMutation,
  type UpsertScanProfileMutation,
  type UpsertScanProfileMutationVariables,
} from 'generated/scan.generated';
import { ScanType, type TerraConnection } from 'generated/types';

export const useListScanResults = (cursor?: string | null) => {
  const { handleError } = useResponseHandler();
  const { scan_type } = useScanResults();
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id ?? '';

  const response = useListScanResultsQuery({
    variables: {
      input: {
        vitall_patient_id,
        scan_type: scan_type ?? ScanType.face,
        cursor,
        include_members: false,
      },
      is_face_or_body:
        scan_type === ScanType.face || scan_type === ScanType.body,
      is_cough: scan_type === ScanType.respiratory,
      is_skin: scan_type === ScanType.skin,
    },
    skip: !vitall_patient_id || !scan_type,
  });
  const { data, error } = response;
  handleError(error);
  const scanResults = data?.listScanResults.data.map((scanResult) => {
    return {
      ...scanResult,
      observations: scanResult.observations?.map(
        (o) => new ObservationRecord(o)
      ),
    };
  });
  const paginatedData = {
    ...data?.listScanResults,
    data: scanResults,
  };
  return { ...response, data: paginatedData };
};

export const useGetDeviceConnections = () => {
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id ?? '';
  const response = useListDeviceConnectionsQuery({
    variables: {
      vitall_patient_id,
    },
    skip: !vitall_patient_id,
  });
  const terraConnections = response.data?.listTerraConnections
    .filter(
      (
        c
      ): c is {
        connection: TerraConnection;
      } => Boolean(c.connection)
    )
    .map(
      ({
        connection: {
          created_at,
          integration,
          last_webhook_update = null,
          terra_connection_id,
        },
      }) => {
        return {
          created_at,
          integration,
          last_webhook_update,
          terra_connection_id,
        };
      }
    );
  const scanAppConnections = response.data?.listScanAppConnections.map(
    ({ integration, last_updated }) => ({
      integration,
      created_at: '',
      last_webhook_update: last_updated,
      terra_connection_id: '',
    })
  );
  return {
    ...response,
    data: [...(terraConnections ?? []), ...(scanAppConnections ?? [])],
  };
};

export const useGetScanProfile = () => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetScanProfileQuery({
    variables: { input: { patient: patient_id } },
    skip: !patient_id,
  });
  return {
    ...response,
    data: response.data?.getScanProfile,
  };
};

export function useUpsertScanProfile(): [
  (
    params: UpsertScanProfileMutationVariables
  ) => Promise<FetchResult<UpsertScanProfileMutation>>,
  boolean,
] {
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'upsertScanProfile',
  });
  const [mutate, { loading: baseLoading }] = useUpsertScanProfileMutation();
  const loading = baseLoading;

  async function upsertScanProfile(params: UpsertScanProfileMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.upsertScanProfile) {
          return;
        }
        const { patient_id } = params.input;
        const normalizedId = cache.identify({
          patient_id,
          __typename: 'ScanProfile',
        });
        // If there is no ScanProfile in cache, add it since we're posting
        if (!normalizedId) {
          const storeObject =
            response.upsertScanProfile as unknown as StoreObject;
          try {
            cache.modify({
              fields: {
                getScanProfile: (_, { toReference }) => {
                  return toReference(storeObject);
                },
              },
            });
          } catch (e) {
            if (isDevelopment) {
              // eslint-disable-next-line no-console
              console.error(e);
            }
          }
        }
      },
    });
    const { errors } = response;
    if (errors) {
      handleError(errors);
    }
    return response;
  }
  return [upsertScanProfile, loading];
}
